import { Curtain } from '../../../components/curtain/Curtain';
import { ErrorToastType, PopupType } from '../../../components/popup/types';
import { CrossOriginCommunication } from '../../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { Logger } from '../../../logger/Logger';
import { betActions } from '../../../store/slices/bets/slice';
import type { IBetSpots } from '../../../store/slices/bets/types';
import { gameStateActions } from '../../../store/slices/game/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { toastActions } from '../../../store/slices/toast/slice';
import { OutgoingMessage } from '../../crossOriginCommunication/types';
import type { GameCommunicationErrorMessageType } from '../types';
import { BetValidationErrors, MessageName } from '../types';

import { BaseBetGameCommunicationProcessor } from './BaseBetGameCommunicationProcessor';

interface BetValidationErrorMessage extends GameCommunicationErrorMessageType {
  betCode: string;
  code: string;
  desc: string;
  extendedErrorCode?: string;
}

export class CoreBetValidationErrorGameCommunicationProcessor extends BaseBetGameCommunicationProcessor {
  public messageName = MessageName.BetValidationError;
  public delay = 0;

  public process(message: BetValidationErrorMessage): void {
    const state = this.store.getState();
    const betCode = Number.parseInt(message.betCode);
    const errorCode = Number.parseInt(message.code);
    const acceptedBets = state.bets.acceptedBets;

    // ToDo: Should be done batched
    const updatedAcceptedBets = acceptedBets.map((bet) => {
      if (bet && bet.betCode === betCode && bet.errorCode !== 0) {
        this.store.dispatch(betActions.wipeBet(betCode));
        return { ...bet, errorCode };
      }
      return bet;
    });

    switch (errorCode) {
      case BetValidationErrors.BetNotAllowed:
        // Bet not allowed
        this.store.dispatch(toastActions.createToast(ErrorToastType.BET_NOT_ALLOWED));
        break;
      case BetValidationErrors.BetNotOnTime:
        // Bet not on time
        this.store.dispatch(toastActions.createToast(ErrorToastType.BET_NOT_ON_TIME));
        break;
      /** currently this error message is only used for Sisal D-BOX integration */
      case BetValidationErrors.ExternalIgnoreError:
        if (message?.extendedErrorCode === '9018') {
          Curtain.resurrect();
          this.store.dispatch(popupActions.overwritePopup(PopupType.SessionExpired));
          this.store.dispatch(gameStateActions.setGameAbandoned(true));
        }
        break;
      case BetValidationErrors.InsufficientBalance:
        // Insufficient funds
        this.store.dispatch(popupActions.pushPopup(PopupType.InsufficientFunds));
        break;
      case BetValidationErrors.PlayerLimitReached:
        // Player limit reached
        this.store.dispatch(toastActions.createToast(ErrorToastType.PLAYER_LIMIT_REACHED));
        break;
      case BetValidationErrors.RealityCheck:
      case BetValidationErrors.DidntMeetMinimumTableLimit:
      case BetValidationErrors.DidntMeetMinimumBetLimit:
      case BetValidationErrors.MaximumBetLimitExceeded:
      case BetValidationErrors.MainBetNotAvailable:
      case BetValidationErrors.FreeChipUnknownError:
      case BetValidationErrors.FreeChipCampaignError:
      case BetValidationErrors.PlayerBonusBetWithoutMainBet:
      case BetValidationErrors.BankerBonusBetWithoutMainBet:
        // this.store.dispatch(toastActions.createToast(ErrorToastType.BETS_REJECTED));
        break;
      case BetValidationErrors.RequestTimeout:
      case BetValidationErrors.InternalServerError:
        this.store.dispatch(popupActions.pushPopup(PopupType.GenericErrorNoRefresh));
        break;
      // Commented out cases that anyway show generic error popup
      // case BetValidationErrors.DidntMeetMaximumTableLimit:
      // case BetValidationErrors.ExternalRetryError:
      // case BetValidationErrors.GameIdMismatch:
      // case BetValidationErrors.GameNotFound:
      // case BetValidationErrors.InvalidHashCode:
      // case BetValidationErrors.InvalidToken:
      // case BetValidationErrors.OperatorInternalError:
      // case BetValidationErrors.PlayerNotFound:
      // case BetValidationErrors.UnknownError:
      default:
        this.store.dispatch(popupActions.pushPopup(PopupType.GenericError));
        break;
    }

    this.store.dispatch(betActions.setAcceptedBets(updatedAcceptedBets));

    const newBetHead: IBetSpots = acceptedBets.reduce((accumulator, bet) => {
      if (bet.errorCode === 0) {
        accumulator[bet.betCode] = { value: bet.amount, type: bet.type, count: bet.count };
      }
      return accumulator;
    }, {} as IBetSpots);

    this.store.dispatch(
      betActions.updateBetStackHead({
        betSpots: newBetHead,
      })
    );

    this.updateGameStatus();
    const { optExtErrorCode, optExtErrorMsg, category, severity } = message;

    CrossOriginCommunication.dispatchWithDebounce(OutgoingMessage.ErrorMessage, {
      errorCode: optExtErrorCode,
      errorMessage: optExtErrorMsg,
      errorParameters: {
        category,
        severity,
      },
    });

    Logger.logFailure('gameCommunicationError', {
      place: 'GameCommunicationBetValidationErrorProcessor',
      additional: errorCode.toString(),
    });
  }
}
